import { React, useState, useEffect } from "react";
import AppWrapper from "../AppWrapper";
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  FormLayout,
  InlineGrid,
  InlineStack,
  Modal,
  Page,
  Select,
  Spinner,
  Text,
  TextField,
} from "@shopify/polaris";
import { PlusIcon, DeleteIcon } from "@shopify/polaris-icons";
import { useParams } from "react-router-dom";
import supabase from "../../supabase-client";
import { stateList } from "../../utility/GlobalVariables";
import { clabeBankName, isValidClabe, clabeDigits } from "../../utility/clabe";
import ClabeBadge from "./ClabeBadge";
import { useProviderLive } from "../../utility/useProviderLive";
import { overrideVerification, speiEnabled, messageFor } from "../../utility/pagos";
import { UserAuth } from "../../context/AuthContext";

function ViewServiceProvider() {
  const { providerId } = useParams();
  const { hasPermission } = UserAuth();
  const [provider, setProvider] = useState(undefined);

  // Verification resolves asynchronously (~78s), so fold the change in rather
  // than leaving the badge stale. Only the verification fields are taken: the
  // form may hold unsaved edits and a websocket push must not clobber them.
  useProviderLive(async (row) => {
    if (String(row.id) === String(providerId) && row.active_verification_id) {
      const { data: v } = await supabase
        .from("bank_account_verifications")
        .select("holder_name, holder_id, match_result, match_score, resolved_at, overridden_by, override_reason")
        .eq("id", row.active_verification_id)
        .maybeSingle();
      setVerification(v || null);
    }
    setProvider((prev) =>
      prev && String(prev.id) === String(row.id)
        ? {
            ...prev,
            clabe_status: row.clabe_status,
            bank_holder_name: row.bank_holder_name,
            clabe_verified_at: row.clabe_verified_at,
            active_verification_id: row.active_verification_id,
          }
        : prev
    );
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Locations
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [editingLocationId, setEditingLocationId] = useState(null);
  // What the banking system reported about this account. Lives on the
  // verification row, not on the provider, so it is fetched alongside.
  const [verification, setVerification] = useState(null);
  // Past overrides on OTHER CLABEs of this provider. An override never carries
  // over to a new account — the trigger clears the verification and the account
  // is checked again from scratch — but when the bank keeps the same wrong RFC
  // on file, the same discrepancy reappears on every account the provider opens.
  // Showing the earlier decision turns that into one informed click instead of
  // a fresh investigation.
  const [priorOverrides, setPriorOverrides] = useState([]);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [overriding, setOverriding] = useState(false);
  const [overrideError, setOverrideError] = useState(null);

  const submitOverride = async () => {
    setOverriding(true);
    setOverrideError(null);
    try {
      await overrideVerification("service_provider", parseInt(providerId), overrideReason);
      setOverrideOpen(false);
      setOverrideReason("");
      // The realtime subscription refreshes the badge and the panel.
    } catch (err) {
      setOverrideError(err.message || messageFor(err.code));
    } finally {
      setOverriding(false);
    }
  };
  const [newLocation, setNewLocation] = useState(null);

  const fetchLocations = async () => {
    setLocationsLoading(true);
    const { data, error } = await supabase
      .from("provider_locations")
      .select("*")
      .eq("provider_id", providerId)
      .order("is_default", { ascending: false });
    if (error) {
      console.error(error);
    } else {
      setLocations(data || []);
    }
    setLocationsLoading(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      const { data, error } = await supabase
        .from("service_providers")
        .select("*")
        .eq("id", providerId)
        .single();
      if (error) {
        console.error(error);
        return;
      }
      setProvider(data);

      if (data?.active_verification_id) {
        const { data: v } = await supabase
          .from("bank_account_verifications")
          .select("holder_name, holder_id, match_result, match_score, resolved_at, overridden_by, override_reason")
          .eq("id", data.active_verification_id)
          .maybeSingle();
        setVerification(v || null);
      }

      const { data: prior } = await supabase
        .from("bank_account_verifications")
        .select("id, holder_id, override_reason, resolved_at, clabe")
        .eq("owner_type", "service_provider")
        .eq("owner_id", providerId)
        .not("overridden_by", "is", null)
        .order("resolved_at", { ascending: false })
        .limit(5);
      setPriorOverrides(prior || []);
    };

    if (!provider) {
      fetchData();
      fetchLocations();
    }
  }, []);

  const handleInputChange = (value, id) => {
    setProvider({ ...provider, [id]: value });
  };

  const providerInfoComponent = () => {
    return (
      <Card>
        <Text></Text>
        <FormLayout>
          <FormLayout.Group>
            <TextField
              label="Razon Social"
              requiredIndicator
              value={provider.name}
              id="name"
              onChange={handleInputChange}
              readOnly={!isEditing}
            />
            <TextField
              label="RFC"
              requiredIndicator
              value={provider.rfc}
              id="rfc"
              onChange={handleInputChange}
              readOnly={!isEditing}
            />
          </FormLayout.Group>
          <Divider />
          <FormLayout.Group>
            <TextField
              label="Nombre de Contacto"
              requiredIndicator
              value={provider.contact_name}
              id="contact_name"
              onChange={handleInputChange}
              readOnly={!isEditing}
            />
            <TextField
              label="Correo"
              requiredIndicator
              value={provider.email}
              id="email"
              onChange={handleInputChange}
              readOnly={!isEditing}
            />
            <TextField
              label="Telefono"
              requiredIndicator
              value={provider.phone}
              id="phone"
              onChange={handleInputChange}
              readOnly={!isEditing}
            />
          </FormLayout.Group>
          <Divider />
          <FormLayout.Group>
            <TextField
              label="Calle y Numero"
              requiredIndicator
              value={provider.street_1}
              id="street_1"
              onChange={handleInputChange}
              readOnly={!isEditing}
            />
            <TextField
              label="Colonia"
              requiredIndicator
              value={provider.street_2}
              id="street_2"
              onChange={handleInputChange}
              readOnly={!isEditing}
            />
            <TextField
              label="Ciudad"
              requiredIndicator
              value={provider.city}
              id="city"
              onChange={handleInputChange}
              readOnly={!isEditing}
            />
            <TextField
              label="Codigo Postal"
              requiredIndicator
              value={provider.cp}
              id="cp"
              onChange={handleInputChange}
              readOnly={!isEditing}
            />
            <Select
              label="Estado"
              requiredIndicator
              value={provider.state}
              id="state"
              onChange={handleInputChange}
              options={stateList}
              disabled={!isEditing}
            />
          </FormLayout.Group>
          <Divider />
          <FormLayout.Group>
            <TextField
              label="Banco"
              value={
                (provider.clabe_status === "verified" && provider.banco) ||
                clabeBankName(provider.clabe) ||
                provider.banco ||
                "—"
              }
              disabled
              helpText={
                provider.clabe_status === "verified"
                  ? "Reportado por el banco al verificar la cuenta."
                  : "Se deduce de los primeros 3 dígitos de la CLABE."
              }
            />

            <TextField
              label="Clabe Interbancaria"
              requiredIndicator
              value={provider.clabe}
              id="clabe"
              onChange={handleInputChange}
              readOnly={!isEditing}
              helpText={
                provider.clabe_status === "verified" && provider.bank_holder_name
                  ? `Titular según el banco: ${provider.bank_holder_name}`
                  : provider.clabe_status === "name_mismatch" && provider.bank_holder_name
                  ? `El banco reporta otro titular: ${provider.bank_holder_name}`
                  : provider.clabe_status
                  ? undefined
                  : "Sin verificar. Se verifica automáticamente al guardar una CLABE nueva."
              }
              error={
                provider.clabe && clabeDigits(provider.clabe).length === 18 &&
                !isValidClabe(provider.clabe)
                  ? "La CLABE no pasa el dígito verificador — revisa que esté bien escrita."
                  : undefined
              }
            />
          </FormLayout.Group>

          {verification && (
            <>
              <Divider />
              {bankReportPanel()}
            </>
          )}
        </FormLayout>
      </Card>
    );
  };

  // What the banking system said about this account. Shown verbatim — it is the
  // only independent evidence of who owns the CLABE, and on a mismatch it is
  // the thing a human has to actually look at.
  const bankReportPanel = () => {
    const ourRfc = (provider.rfc || "").toUpperCase();
    const bankRfc = (verification.holder_id || "").toUpperCase();
    // Same bank-reported RFC, different verification: the discrepancy this
    // provider's bank keeps producing, already judged once.
    const repeatOverride = (priorOverrides || []).find(
      (o) =>
        String(o.id) !== String(provider?.active_verification_id) &&
        (o.holder_id || "").toUpperCase() === bankRfc &&
        o.override_reason
    );
    const rfcDiffers = !!bankRfc && !!ourRfc && bankRfc !== ourRfc;
    // First 10 chars of an RFC come from name + birthdate; the last 3 are
    // SAT's homoclave. Matching on the first 10 means the same person with a
    // mistyped homoclave — a very different situation from a different entity.
    const sameRoot = rfcDiffers && bankRfc.slice(0, 10) === ourRfc.slice(0, 10);

    return (
      <BlockStack gap={200}>
        <Text variant="headingSm" as="h3">Lo que reportó el banco</Text>

        <InlineStack align="space-between" blockAlign="start" gap={400}>
          <Text tone="subdued">Titular</Text>
          <Text alignment="end" fontWeight="semibold">
            {verification.holder_name || "—"}
          </Text>
        </InlineStack>

        <InlineStack align="space-between" blockAlign="start" gap={400}>
          <Text tone="subdued">RFC</Text>
          <Text alignment="end" fontWeight="semibold" tone={rfcDiffers ? "critical" : undefined}>
            {verification.holder_id || "no reportado"}
          </Text>
        </InlineStack>

        {rfcDiffers && (
          <InlineStack align="space-between" blockAlign="start" gap={400}>
            <Text tone="subdued">RFC registrado por nosotros</Text>
            <Text alignment="end">{ourRfc}</Text>
          </InlineStack>
        )}

        {rfcDiffers && sameRoot && (
          <Banner tone="warning" title="Solo difiere la homoclave">
            <p>
              Los primeros 10 caracteres coinciden, así que se trata de la misma
              persona: lo más probable es que la homoclave que tenemos registrada
              esté mal. Verifica contra su constancia de situación fiscal y
              corrige el RFC aquí; la cuenta se volverá a verificar sola.
            </p>
          </Banner>
        )}

        {rfcDiffers && !sameRoot && (
          <Banner tone="critical" title="La cuenta es de otro contribuyente">
            <p>
              El banco reporta que esta CLABE pertenece a{" "}
              <b>{verification.holder_name}</b> ({bankRfc}), que no es este
              proveedor. No se puede pagar hasta aclararlo: puede ser una CLABE
              equivocada o una cuenta de un tercero.
            </p>
          </Banner>
        )}

        {verification.overridden_by ? (
          <Banner tone="info" title="Cuenta autorizada manualmente">
            <p>{verification.override_reason}</p>
          </Banner>
        ) : (
          rfcDiffers &&
          speiEnabled() &&
          hasPermission("services.pay") && (
            <BlockStack gap={300}>
              {repeatOverride && (
                <Banner tone="warning" title="Ya autorizaste esta misma diferencia antes">
                  <p>
                    El{" "}
                    {new Date(repeatOverride.resolved_at).toLocaleDateString("es-MX", {
                      day: "2-digit", month: "short", year: "numeric",
                    })}{" "}
                    se aceptó este mismo RFC del banco ({bankRfc}) para otra
                    CLABE de este proveedor, con este motivo:
                  </p>
                  <p style={{ fontStyle: "italic" }}>{repeatOverride.override_reason}</p>
                  <p>
                    La autorización no se hereda: cada cuenta se revisa por
                    separado, porque una CLABE nueva es dinero a un destino
                    nuevo. Si esta también es suya, puedes autorizarla otra vez.
                  </p>
                </Banner>
              )}
              <Button
                onClick={() => {
                  // Prefilled, not auto-accepted: the decision still needs a
                  // person, but they should not have to retype what they
                  // already concluded about this same provider.
                  setOverrideReason(repeatOverride?.override_reason || "");
                  setOverrideOpen(true);
                }}
              >
                Autorizar esta cuenta de todos modos
              </Button>
            </BlockStack>
          )
        )}

        {verification.resolved_at && (
          <Text tone="subdued" variant="bodySm">
            Verificado el{" "}
            {new Date(verification.resolved_at).toLocaleString("es-MX", {
              day: "2-digit", month: "short", year: "numeric",
              hour: "2-digit", minute: "2-digit",
            })}
            {verification.match_score != null &&
              ` · coincidencia de nombre ${Number(verification.match_score).toFixed(2)}`}
          </Text>
        )}
      </BlockStack>
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    // Keep `banco` consistent with the CLABE being saved. If the CLABE changed,
    // the trigger clears the verification anyway and the re-verification will
    // overwrite this with the institution the bank actually reports.
    const { error } = await supabase
      .from("service_providers")
      .update({ ...provider, banco: clabeBankName(provider.clabe) || provider.banco })
      .eq("id", providerId);
    if (error) {
      console.error(error);
      setIsSaving(false);
      return;
    }
    setIsEditing(false);
  };

  // Coordinates are held as strings in the form; coerce to a number (or null)
  // before writing to the DB.
  const toCoord = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // What to render in a coordinate field: the raw value when set; empty while
  // editing (so the user can type); "N/A" in read-only mode when unset, so a
  // missing coordinate is unmistakable rather than looking blank.
  const coordFieldValue = (v, editing) => {
    if (v !== null && v !== undefined && v !== "") return String(v);
    return editing ? "" : "N/A";
  };

  // --- Location handlers ---
  const handleNewLocation = () => {
    setNewLocation({
      label: "",
      contact_name: "",
      email: "",
      phone: "",
      street_1: "",
      street_2: "",
      city: "",
      cp: "",
      state: "Nuevo León",
      latitude: "",
      longitude: "",
    });
  };

  const handleSaveNewLocation = async () => {
    const { error } = await supabase.from("provider_locations").insert({
      ...newLocation,
      latitude: toCoord(newLocation.latitude),
      longitude: toCoord(newLocation.longitude),
      provider_id: providerId,
      is_default: locations.length === 0,
    });
    if (error) {
      console.error(error);
      return;
    }
    setNewLocation(null);
    fetchLocations();
  };

  const handleUpdateLocation = async (location) => {
    const { id, created_at, ...updates } = location;
    const { error } = await supabase
      .from("provider_locations")
      .update({
        ...updates,
        latitude: toCoord(updates.latitude),
        longitude: toCoord(updates.longitude),
      })
      .eq("id", id);
    if (error) {
      console.error(error);
      return;
    }
    setEditingLocationId(null);
    fetchLocations();
  };

  const handleDeleteLocation = async (locationId) => {
    const { error } = await supabase
      .from("provider_locations")
      .delete()
      .eq("id", locationId);
    if (error) {
      console.error(error);
      return;
    }
    fetchLocations();
  };

  const handleLocationFieldChange = (locationId, field, value) => {
    setLocations((prev) =>
      prev.map((loc) =>
        loc.id === locationId ? { ...loc, [field]: value } : loc,
      ),
    );
  };

  const locationCard = (location, isNew = false) => {
    const isEditingThis = isNew || editingLocationId === location.id;
    const data = isNew ? newLocation : location;

    const handleChange = isNew
      ? (value, id) => setNewLocation({ ...newLocation, [id]: value })
      : (value, id) => handleLocationFieldChange(location.id, id, value);

    return (
      <Card key={isNew ? "new" : location.id}>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="200" blockAlign="center">
              <Text variant="headingSm">{isNew ? "Nueva Sucursal" : data.label || "Sin nombre"}</Text>
              {!isNew && data.is_default && <Badge tone="info">Principal</Badge>}
            </InlineStack>
            {hasPermission("service.provider.create") && (
              <InlineStack gap="200">
                {isNew ? (
                  <>
                    <Button size="slim" onClick={handleSaveNewLocation}>Guardar</Button>
                    <Button size="slim" tone="critical" onClick={() => setNewLocation(null)}>Cancelar</Button>
                  </>
                ) : isEditingThis ? (
                  <>
                    <Button size="slim" onClick={() => handleUpdateLocation(location)}>Guardar</Button>
                    <Button size="slim" onClick={() => { setEditingLocationId(null); fetchLocations(); }}>Cancelar</Button>
                  </>
                ) : (
                  <>
                    <Button size="slim" onClick={() => setEditingLocationId(location.id)}>Editar</Button>
                    {!data.is_default && (
                      <Button size="slim" tone="critical" icon={DeleteIcon} onClick={() => handleDeleteLocation(location.id)} />
                    )}
                  </>
                )}
              </InlineStack>
            )}
          </InlineStack>
          <FormLayout>
            <FormLayout.Group>
              <TextField label="Nombre" value={data.label} id="label" onChange={handleChange} readOnly={!isEditingThis} />
              <TextField label="Contacto" value={data.contact_name} id="contact_name" onChange={handleChange} readOnly={!isEditingThis} />
              <TextField label="Correo" value={data.email} id="email" onChange={handleChange} readOnly={!isEditingThis} />
              <TextField label="Teléfono" value={data.phone} id="phone" onChange={handleChange} readOnly={!isEditingThis} />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField label="Calle y Número" value={data.street_1} id="street_1" onChange={handleChange} readOnly={!isEditingThis} />
              <TextField label="Colonia" value={data.street_2} id="street_2" onChange={handleChange} readOnly={!isEditingThis} />
              <TextField label="Ciudad" value={data.city} id="city" onChange={handleChange} readOnly={!isEditingThis} />
              <TextField label="C.P." value={data.cp} id="cp" onChange={handleChange} readOnly={!isEditingThis} />
              <Select label="Estado" value={data.state} id="state" onChange={handleChange} options={stateList} disabled={!isEditingThis} />
            </FormLayout.Group>
            <FormLayout.Group
              title="Coordenadas (opcional)"
              helpText="Pega la latitud y longitud desde Google Maps para ubicar esta sucursal con precisión en el mapa. Si se dejan vacías, se ubica por ciudad."
            >
              <TextField
                label="Latitud"
                type={isEditingThis ? "number" : "text"}
                inputMode="decimal"
                step={0.000001}
                placeholder={isEditingThis ? "25.6866" : undefined}
                value={coordFieldValue(data.latitude, isEditingThis)}
                id="latitude"
                onChange={handleChange}
                readOnly={!isEditingThis}
                autoComplete="off"
              />
              <TextField
                label="Longitud"
                type={isEditingThis ? "number" : "text"}
                inputMode="decimal"
                step={0.000001}
                placeholder={isEditingThis ? "-100.3161" : undefined}
                value={coordFieldValue(data.longitude, isEditingThis)}
                id="longitude"
                onChange={handleChange}
                readOnly={!isEditingThis}
                autoComplete="off"
              />
            </FormLayout.Group>
          </FormLayout>
        </BlockStack>
      </Card>
    );
  };

  const locationsSection = () => {
    return (
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingMd">Sucursales</Text>
          {hasPermission("service.provider.create") && !newLocation && (
            <Button icon={PlusIcon} size="slim" onClick={handleNewLocation}>
              Agregar Sucursal
            </Button>
          )}
        </InlineStack>
        {locationsLoading ? (
          <Spinner size="small" />
        ) : (
          <BlockStack gap="300">
            {newLocation && locationCard(null, true)}
            {locations.map((loc) => locationCard(loc))}
            {locations.length === 0 && !newLocation && (
              <Text tone="subdued">No hay sucursales registradas</Text>
            )}
          </BlockStack>
        )}
      </BlockStack>
    );
  };

  const primaryAction = () => {
    if (!provider) return undefined;
    if (!hasPermission("service.provider.create")) return;
    if (!isEditing)
      return { content: "Editar", onAction: () => setIsEditing(true) };
    if (isEditing)
      return {
        content: "Guardar",
        onAction: () => handleSave(),
        loading: isSaving,
      };
  };

  const secondaryActions = () => {
    return [
      {
        content: "Ver Servicios",
        url: `/services?provider=${providerId}`,
      },
    ];
  };

  const pageContent = () => {
    if (!provider) return <Spinner size="small" />;
    return (
      <BlockStack gap="400">
        {providerInfoComponent()}
        {locationsSection()}
      </BlockStack>
    );
  };

  return (
    <AppWrapper>
      <Page
        title="Proveedor"
        titleMetadata={
          <ClabeBadge
            status={provider?.clabe_status}
            holderName={provider?.bank_holder_name}
          />
        }
        backAction={{ url: "/service_providers" }}
        primaryAction={primaryAction()}
        secondaryActions={secondaryActions()}
      >
        {pageContent()}
      </Page>

      <Modal
        open={overrideOpen}
        onClose={() => setOverrideOpen(false)}
        title="Autorizar esta cuenta manualmente"
        primaryAction={{
          content: "Autorizar",
          onAction: submitOverride,
          loading: overriding,
          disabled: overrideReason.trim().length < 10,
        }}
        secondaryActions={[{ content: "Cancelar", onAction: () => setOverrideOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap={300}>
            {overrideError && <Banner tone="critical">{overrideError}</Banner>}
            <Banner tone="warning">
              El banco reporta un RFC distinto al que tenemos registrado. Al
              autorizar, esta cuenta podrá recibir pagos aunque no coincida.
            </Banner>
            <BlockStack gap={100}>
              <Text tone="subdued" variant="bodySm">Titular según el banco</Text>
              <Text fontWeight="semibold">{verification?.holder_name}</Text>
            </BlockStack>
            <BlockStack gap={100}>
              <Text tone="subdued" variant="bodySm">RFC del banco / el nuestro</Text>
              <Text numeric>
                {verification?.holder_id} · {provider?.rfc}
              </Text>
            </BlockStack>
            <TextField
              label="Motivo"
              requiredIndicator
              multiline={3}
              value={overrideReason}
              onChange={setOverrideReason}
              placeholder="Ej. Verifiqué el RFC contra su constancia; el banco tiene una homoclave desactualizada."
              helpText="Queda registrado con tu nombre y la fecha."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </AppWrapper>
  );
}

export default ViewServiceProvider;
